/**
 * My Courses Page
 * Displays the courses that the current user has enrolled in
 */

import React, { useState, useEffect, useCallback, useMemo, useRef, useDeferredValue } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import enrollmentService from '../services/enrollmentService';
import { EnrolledCourse } from '../types/enrollment';
import SearchBar, { SearchBarRef } from '../components/common/SearchBar';
import './MyCoursesPage.css';

import { resolveMediaUrl, getSmallThumbnailUrl } from '../utils/mediaUrl';
import { BilingualTitle } from '../components/common/BilingualTitle';

const MyCoursesPage: React.FC = () => {
  const navigate = useNavigate();
  const { t } = useTranslation(['course', 'common']);
  const [enrollments, setEnrollments] = useState<EnrolledCourse[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const searchBarRef = useRef<SearchBarRef>(null);

  // Use deferred value for better performance during typing
  const deferredSearchQuery = useDeferredValue(searchQuery);

  const loadEnrollments = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await enrollmentService.getMyEnrollments();
      setEnrollments(data);
    } catch (err) {
      console.error('Failed to load enrollments:', err);
      const message = err instanceof Error ? err.message : t('course:myCourses.failedToLoad');
      setError(message);
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    loadEnrollments();
  }, [loadEnrollments]);

  // Filter enrollments based on deferred search query (local filtering)
  const filteredEnrollments = useMemo(() => {
    if (!deferredSearchQuery.trim()) return enrollments;

    const query = deferredSearchQuery.toLowerCase().trim();
    return enrollments.filter((enrollment) => {
      const title = (enrollment.course?.title || '').toLowerCase();
      const description = (enrollment.course?.description || '').toLowerCase();
      const creator = (enrollment.course?.creator?.fullName || enrollment.course?.creator?.username || '').toLowerCase();

      return title.includes(query) || description.includes(query) || creator.includes(query);
    });
  }, [enrollments, deferredSearchQuery]);

  const handleClearSearch = () => {
    setSearchQuery('');
  };

  const formatDuration = (seconds?: number): string => {
    if (!seconds) return '--:--';
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${String(secs).padStart(2, '0')}`;
  };

  const getCefrBadgeClass = (cefrLevel?: string): string => {
    if (!cefrLevel) return 'unknown';
    return `cefr-${cefrLevel.toLowerCase()}`;
  };

  if (loading) {
    return (
      <div className="my-courses-page">
        <div className="loading-container">
          <div className="spinner"></div>
          <p>{t('course:myCourses.loading')}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="my-courses-page">
      {error && (
        <div className="error-banner">
          <p>{error}</p>
          <button onClick={loadEnrollments}>{t('common:retry')}</button>
        </div>
      )}

      {/* Search Bar */}
      {enrollments.length > 0 && (
        <div className="my-courses-search-bar">
          <SearchBar
            ref={searchBarRef}
            value={searchQuery}
            onChange={setSearchQuery}
            onClear={handleClearSearch}
            placeholder={t('course:myCourses.searchPlaceholder')}
          />
          {searchQuery && (
            <span className="search-result-count">
              {t('common:searchResultsOf', { filtered: filteredEnrollments.length, total: enrollments.length })}
            </span>
          )}
        </div>
      )}

      {enrollments.length === 0 ? (
        <div className="empty-state">
          <div className="empty-icon">📖</div>
          <h3>{t('course:myCourses.noCourses')}</h3>
          <p>{t('course:myCourses.noCoursesDesc')}</p>
          <Link to="/courses" className="btn-primary">
            {t('common:browseCourses')}
          </Link>
        </div>
      ) : filteredEnrollments.length === 0 ? (
        <div className="empty-state">
          <div className="empty-icon">🔍</div>
          <h3>{t('course:myCourses.noSearchResults')}</h3>
          <p>{t('course:myCourses.noSearchResultsFor', { query: searchQuery })}</p>
          <button className="btn-primary" onClick={handleClearSearch}>
            {t('common:clearSearch')}
          </button>
        </div>
      ) : (
        <div className="courses-grid">
          {filteredEnrollments.map((enrollment) => (
            <div
              key={enrollment.id}
              className="course-card"
              onClick={() => navigate(`/courses/${enrollment.courseId}`)}
            >
              {(enrollment.course?.coverImageUrl || enrollment.course?.thumbnailUrl) ? (
                <div
                  className="course-cover"
                  style={{ backgroundImage: `url(${getSmallThumbnailUrl(enrollment.course.coverImageUrl || enrollment.course.thumbnailUrl)})` }}
                />
              ) : (
                <div className="course-cover placeholder">
                  <span>🎓</span>
                </div>
              )}
              <div className="course-info">
                <BilingualTitle
                  title={enrollment.course?.title || ''}
                  titles={enrollment.course?.titles}
                  fallback={t('course:manage.untitledCourse')}
                  variant="card"
                  as="h3"
                  className="course-title"
                />
                <p className="course-description">
                  {enrollment.course?.description || t('common:noDescription')}
                </p>
                <div className="course-meta">
                  {enrollment.course?.cefrLevel && (
                    <span className={`badge difficulty ${getCefrBadgeClass(enrollment.course.cefrLevel)}`}>
                      {enrollment.course.cefrLevel}
                    </span>
                  )}
                  <span className="badge duration">
                    {formatDuration(enrollment.course?.durationSeconds)}
                  </span>
                  {(enrollment.course?.wordCount !== undefined && enrollment.course.wordCount > 0) && (
                    <span className="badge words">{t('common:words', { count: enrollment.course.wordCount })}</span>
                  )}
                </div>
                <div className="course-content-stats">
                  {enrollment.course?.sentenceCount !== undefined && enrollment.course.sentenceCount > 0 && (
                    <span className="content-stat">{t('common:sentences', { count: enrollment.course.sentenceCount })}</span>
                  )}
                  {enrollment.course?.vocabularyCount !== undefined && enrollment.course.vocabularyCount > 0 && (
                    <span className="content-stat">{t('common:vocabulary', { count: enrollment.course.vocabularyCount })}</span>
                  )}
                  {enrollment.course?.phraseCount !== undefined && enrollment.course.phraseCount > 0 && (
                    <span className="content-stat">{t('common:phrases', { count: enrollment.course.phraseCount })}</span>
                  )}
                </div>
                <div className="course-stats">
                  <span className="stat">
                    <span className="stat-icon">👥</span>
                    {t('common:enrolled', { count: enrollment.course?.enrollmentCount || 0 })}
                  </span>
                </div>
                {enrollment.course?.creator && (enrollment.course.creator.fullName || enrollment.course.creator.username) && (
                  <div className="course-creator">
                    <span>{t('common:by')}</span>
                    <Link
                      to={`/profile/${enrollment.course.creator.id}`}
                      className="creator-link"
                      onClick={(e) => e.stopPropagation()}
                    >
                      {enrollment.course.creator.fullName || enrollment.course.creator.username}
                    </Link>
                  </div>
                )}
                <div className="course-actions">
                  <button
                    className="btn-enter"
                    onClick={(e) => {
                      e.stopPropagation();
                      navigate(`/courses/${enrollment.courseId}`);
                    }}
                  >
                    {t('common:enterCourse')}
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default MyCoursesPage;
